import { useParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const LEGAL_CONTENT = {
  'mentions-legales': {
    title: 'Mentions légales',
    seoTitle: 'Mentions légales | PDF Facile',
    seoDescription: 'Mentions légales du site PDF Facile - Générateur de documents administratifs PDF.',
    content: `
      <h2>Éditeur du site</h2>
      <p>
        Le site <strong>www.pdf-facile.fr</strong> est édité par :<br/><br/>
        <strong>NB MARKETS</strong><br/>
        Statut juridique : Micro-entreprise<br/>
        SIREN : 520 506 726<br/>
        Pays : France<br/><br/>
        Email de contact : <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
      
      <h2>Siège social</h2>
      <p>
        L'adresse du siège social est disponible sur simple demande auprès de notre service client 
        à <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>.
      </p>
      
      <h2>Propriétaire du site</h2>
      <p>
        Le site www.pdf-facile.fr est la propriété exclusive de NB MARKETS, micro-entreprise 
        immatriculée en France.
      </p>
      
      <h2>Hébergement</h2>
      <p>
        Ce site est hébergé par un prestataire technique professionnel assurant la sécurité 
        et la disponibilité du service. Les informations relatives à l'hébergeur peuvent être 
        obtenues sur demande à <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>.
      </p>
      
      <h2>Propriété intellectuelle</h2>
      <p>
        L'ensemble des éléments constituant ce site (textes, graphismes, logiciels, structure, 
        modèles de documents, etc.) sont la propriété exclusive de NB MARKETS ou font l'objet 
        d'une autorisation d'utilisation.
      </p>
      <p>
        Toute reproduction, représentation, modification, publication ou adaptation de tout ou 
        partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite 
        sans autorisation écrite préalable de NB MARKETS.
      </p>
      
      <h2>Responsabilité</h2>
      <p>
        Les modèles de documents proposés sur PDF Facile sont rédigés conformément aux usages 
        administratifs français courants. Ils constituent une aide à la rédaction et ne 
        remplacent pas un conseil juridique personnalisé.
      </p>
      <p>
        <strong>L'utilisateur reste seul responsable :</strong>
      </p>
      <ul>
        <li>De l'exactitude et de la véracité des informations qu'il saisit dans les formulaires</li>
        <li>De l'usage qu'il fait des documents générés</li>
        <li>De la conformité du document à sa situation personnelle</li>
      </ul>
      <p>
        NB MARKETS ne garantit pas l'acceptation des documents par les administrations ou 
        organismes destinataires. L'éditeur ne saurait être tenu responsable des dommages 
        directs ou indirects résultant de l'utilisation des documents générés par le site.
      </p>
      
      <h2>Données personnelles</h2>
      <p>
        Pour plus d'informations sur le traitement de vos données personnelles, veuillez 
        consulter notre <a href="/confidentialite">politique de confidentialité</a>.
      </p>
      
      <h2>Contact</h2>
      <p>
        Pour toute question concernant le site, vous pouvez nous contacter à :<br/>
        <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
    `
  },
  'conditions-utilisation': {
    title: "Conditions générales d'utilisation",
    seoTitle: "Conditions générales d'utilisation | PDF Facile",
    seoDescription: "CGU du site PDF Facile - Conditions d'utilisation du service de génération de documents administratifs PDF.",
    content: `
      <p><em>Dernière mise à jour : décembre 2024</em></p>
      
      <h2>Article 1 – Objet</h2>
      <p>
        Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») ont pour objet de 
        définir les modalités d'accès et d'utilisation du service proposé par le site 
        <strong>www.pdf-facile.fr</strong> (ci-après « le Site »), édité par NB MARKETS.
      </p>
      <p>
        L'utilisation du Site implique l'acceptation pleine et entière des présentes CGU.
      </p>
      
      <h2>Article 2 – Description du service</h2>
      <p>
        PDF Facile est un service en ligne de génération de documents administratifs au format PDF. 
        Le Site permet à l'utilisateur de :
      </p>
      <ul>
        <li>Sélectionner un modèle de document parmi ceux proposés (attestations, lettres de résiliation, etc.)</li>
        <li>Remplir un formulaire avec les informations nécessaires</li>
        <li>Visualiser gratuitement un aperçu partiel du document</li>
        <li>Télécharger le document PDF final après paiement</li>
      </ul>
      
      <h2>Article 3 – Accès au service</h2>
      <p>
        Le service est accessible sans création de compte utilisateur. Aucune inscription n'est 
        requise pour utiliser le Site.
      </p>
      <p>
        L'utilisateur peut consulter les modèles de documents et générer des aperçus gratuitement. 
        Seul le téléchargement du document PDF final est soumis à paiement.
      </p>
      
      <h2>Article 4 – Tarification et paiement</h2>
      <p>
        Le prix de chaque document est de <strong>4,99 € TTC</strong>.
      </p>
      <p>
        Le paiement s'effectue de manière sécurisée par carte bancaire via la plateforme 
        <strong>Stripe</strong>. Il s'agit d'un paiement unique, sans abonnement ni engagement.
      </p>
      <p>
        Le téléchargement du document PDF est disponible immédiatement après validation du paiement.
      </p>
      
      <h2>Article 5 – Droit de rétractation</h2>
      <p>
        Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation 
        ne peut être exercé pour les contrats de fourniture d'un contenu numérique non fourni 
        sur un support matériel dont l'exécution a commencé après accord préalable exprès du 
        consommateur et renoncement exprès à son droit de rétractation.
      </p>
      <p>
        En procédant au paiement, l'utilisateur reconnaît avoir été informé de cette disposition 
        et accepte expressément que l'exécution du service commence immédiatement.
      </p>
      
      <h2>Article 6 – Responsabilité de l'utilisateur</h2>
      <p>
        L'utilisateur est seul responsable de :
      </p>
      <ul>
        <li>L'exactitude et la véracité des informations qu'il saisit dans les formulaires</li>
        <li>L'usage qu'il fait des documents générés</li>
        <li>La vérification de l'adéquation du document à sa situation personnelle</li>
      </ul>
      <p>
        L'utilisateur s'engage à ne pas utiliser le service à des fins frauduleuses, illégales 
        ou contraires aux bonnes mœurs. Toute fausse déclaration engage la responsabilité pénale 
        de son auteur.
      </p>
      
      <h2>Article 7 – Limitation de responsabilité</h2>
      <p>
        Les documents générés par PDF Facile sont des modèles types conformes aux usages 
        administratifs français. Ils constituent une aide à la rédaction et ne se substituent 
        pas à un conseil juridique personnalisé.
      </p>
      <p>
        <strong>NB MARKETS ne garantit pas l'acceptation des documents par les administrations, 
        organismes ou tiers destinataires.</strong> Chaque organisme peut avoir des exigences 
        spécifiques non couvertes par nos modèles.
      </p>
      <p>
        NB MARKETS ne saurait être tenu responsable des dommages directs ou indirects résultant 
        de l'utilisation ou de l'impossibilité d'utiliser les documents générés.
      </p>
      
      <h2>Article 8 – Propriété intellectuelle</h2>
      <p>
        Les modèles de documents, le logiciel et l'ensemble des éléments du Site sont la 
        propriété exclusive de NB MARKETS.
      </p>
      <p>
        L'utilisateur acquiert uniquement le droit d'utiliser le document PDF généré pour son 
        usage personnel. Toute revente, redistribution ou exploitation commerciale des documents 
        est interdite.
      </p>
      
      <h2>Article 9 – Modification des CGU</h2>
      <p>
        NB MARKETS se réserve le droit de modifier les présentes CGU à tout moment. Les 
        modifications entrent en vigueur dès leur publication sur le Site. Il appartient à 
        l'utilisateur de consulter régulièrement les CGU.
      </p>
      
      <h2>Article 10 – Droit applicable et juridiction</h2>
      <p>
        Les présentes CGU sont soumises au droit français.
      </p>
      <p>
        En cas de litige relatif à l'interprétation ou l'exécution des présentes, les parties 
        s'efforceront de trouver une solution amiable. À défaut, les tribunaux français seront 
        seuls compétents.
      </p>
      
      <h2>Article 11 – Contact</h2>
      <p>
        Pour toute question relative aux présentes CGU :<br/>
        <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
    `
  },
  'confidentialite': {
    title: 'Politique de confidentialité',
    seoTitle: 'Politique de confidentialité | PDF Facile',
    seoDescription: 'Politique de confidentialité et protection des données personnelles du site PDF Facile.',
    content: `
      <p><em>Dernière mise à jour : décembre 2024</em></p>
      
      <p>
        La présente politique de confidentialité décrit la manière dont NB MARKETS 
        (ci-après « nous ») collecte, utilise et protège vos données personnelles dans le 
        cadre de l'utilisation du site <strong>www.pdf-facile.fr</strong> (ci-après « le Site »).
      </p>
      <p>
        Nous nous engageons à respecter la réglementation applicable en matière de protection 
        des données personnelles, notamment le Règlement Général sur la Protection des Données 
        (RGPD) et la loi Informatique et Libertés.
      </p>
      
      <h2>1. Responsable du traitement</h2>
      <p>
        Le responsable du traitement des données personnelles est :<br/><br/>
        <strong>NB MARKETS</strong><br/>
        Micro-entreprise – SIREN : 520 506 726<br/>
        Email : <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
      
      <h2>2. Données collectées</h2>
      <p>
        Dans le cadre de l'utilisation du Site, nous collectons uniquement les données 
        strictement nécessaires à la fourniture du service :
      </p>
      <ul>
        <li><strong>Données saisies dans les formulaires :</strong> nom, prénom, adresse, 
        et autres informations nécessaires à la génération du document demandé</li>
        <li><strong>Données de paiement :</strong> traitées directement par notre prestataire 
        de paiement Stripe (nous n'avons pas accès à vos coordonnées bancaires complètes)</li>
        <li><strong>Données techniques :</strong> adresse IP, type de navigateur, pages 
        consultées (à des fins de sécurité et d'amélioration du service)</li>
      </ul>
      <p>
        <strong>Aucun compte utilisateur n'est créé.</strong> Nous ne collectons pas d'adresse 
        email à des fins de prospection commerciale.
      </p>
      
      <h2>3. Finalités du traitement</h2>
      <p>
        Vos données sont collectées et utilisées exclusivement pour :
      </p>
      <ul>
        <li>Générer le document PDF demandé</li>
        <li>Traiter le paiement de votre commande</li>
        <li>Vous permettre de télécharger votre document</li>
        <li>Assurer la sécurité et le bon fonctionnement du Site</li>
        <li>Répondre à vos éventuelles demandes de contact</li>
      </ul>
      
      <h2>4. Base légale du traitement</h2>
      <p>
        Le traitement de vos données repose sur :
      </p>
      <ul>
        <li><strong>L'exécution du contrat :</strong> les données saisies dans le formulaire 
        sont nécessaires à la génération de votre document</li>
        <li><strong>L'obligation légale :</strong> conservation des données de facturation 
        conformément aux obligations comptables</li>
        <li><strong>L'intérêt légitime :</strong> sécurité du Site et amélioration du service</li>
      </ul>
      
      <h2>5. Destinataires des données</h2>
      <p>
        Vos données personnelles ne sont <strong>jamais vendues ni louées à des tiers</strong>.
      </p>
      <p>
        Elles peuvent être transmises uniquement aux prestataires suivants, dans le cadre 
        strict de leurs missions :
      </p>
      <ul>
        <li><strong>Stripe :</strong> pour le traitement sécurisé des paiements 
        (certifié PCI-DSS)</li>
        <li><strong>Notre hébergeur :</strong> pour le stockage technique des données</li>
      </ul>
      
      <h2>6. Durée de conservation</h2>
      <p>
        Nous conservons vos données personnelles pendant la durée strictement nécessaire :
      </p>
      <ul>
        <li><strong>Données du formulaire :</strong> supprimées automatiquement 24 heures 
        après la génération du document</li>
        <li><strong>Données de transaction :</strong> conservées 10 ans conformément aux 
        obligations comptables et fiscales françaises</li>
        <li><strong>Données techniques :</strong> conservées 12 mois maximum</li>
      </ul>
      
      <h2>7. Sécurité des données</h2>
      <p>
        Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour 
        protéger vos données :
      </p>
      <ul>
        <li>Connexion sécurisée HTTPS sur l'ensemble du Site</li>
        <li>Paiements sécurisés via Stripe (certification PCI-DSS)</li>
        <li>Accès restreint aux données personnelles</li>
        <li>Serveurs sécurisés</li>
      </ul>
      
      <h2>8. Vos droits</h2>
      <p>
        Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
      </p>
      <ul>
        <li><strong>Droit d'accès :</strong> obtenir confirmation du traitement de vos données 
        et en recevoir une copie</li>
        <li><strong>Droit de rectification :</strong> demander la correction de données 
        inexactes</li>
        <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données 
        (sous réserve des obligations légales de conservation)</li>
        <li><strong>Droit à la limitation :</strong> demander la suspension du traitement</li>
        <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format 
        structuré</li>
        <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
      </ul>
      <p>
        Pour exercer ces droits, contactez-nous à : 
        <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
      <p>
        Nous nous engageons à répondre à votre demande dans un délai d'un mois.
      </p>
      
      <h2>9. Cookies</h2>
      <p>
        Le Site utilise uniquement des <strong>cookies techniques strictement nécessaires</strong> 
        au fonctionnement du service (gestion de session, sécurité).
      </p>
      <p>
        Aucun cookie publicitaire, de tracking ou de profilage n'est utilisé.
      </p>
      
      <h2>10. Réclamation</h2>
      <p>
        Si vous estimez que le traitement de vos données ne respecte pas la réglementation, 
        vous pouvez introduire une réclamation auprès de la CNIL (Commission Nationale de 
        l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" 
        rel="noopener noreferrer">www.cnil.fr</a>
      </p>
      
      <h2>11. Contact</h2>
      <p>
        Pour toute question relative à la protection de vos données personnelles :<br/>
        <strong>Email :</strong> <a href="mailto:contact@pdf-facile.fr">contact@pdf-facile.fr</a>
      </p>
      
      <h2>12. Modification de la politique</h2>
      <p>
        Nous nous réservons le droit de modifier la présente politique de confidentialité à 
        tout moment. Les modifications entrent en vigueur dès leur publication sur le Site. 
        Nous vous invitons à consulter régulièrement cette page.
      </p>
    `
  }
};

export default function LegalPage() {
  const { pageType } = useParams();
  const pageData = LEGAL_CONTENT[pageType];
  
  if (!pageData) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Page non trouvée</h1>
            <Link to="/" className="btn-primary inline-block">
              Retour à l'accueil
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{pageData.seoTitle}</title>
        <meta name="description" content={pageData.seoDescription} />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        
        <main className="flex-1">
          <section className="bg-slate-50 py-6 md:py-8">
            <div className="container-app">
              <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Link to="/" className="hover:text-primary">Accueil</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-foreground font-medium">{pageData.title}</span>
              </nav>
              
              <h1 className="text-2xl sm:text-3xl font-bold text-primary" data-testid="legal-page-title">
                {pageData.title}
              </h1>
            </div>
          </section>
          
          <section className="section-spacing">
            <div className="container-app max-w-3xl">
              <div 
                className="prose prose-slate max-w-none
                  prose-headings:font-bold prose-headings:text-slate-900
                  prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:text-slate-600 prose-ul:mb-4 prose-li:my-1
                  prose-a:text-accent prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-800"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
